import { diffArrays, diffChars } from "diff";
import { escapeHtml } from "./comments";
import { splitBlocks } from "./editor-core";

/** 版本差异纯逻辑：段落级对齐 + 修改段内字符级混排，供历史面板与冲突对比复用。 */

export type DiffSegment = { type: "same" | "ins" | "del"; text: string };

export type DiffBlock =
  | { kind: "same"; text: string }
  | { kind: "added"; text: string }
  | { kind: "removed"; text: string }
  | { kind: "changed"; segments: DiffSegment[] };

/** 修改段的相似度低于此值时放弃行内混排，退化为整段删+增（避免满屏红绿碎块）。 */
const MIN_INLINE_SIMILARITY = 0.3;

function pairBlocks(oldPara: string, newPara: string): DiffBlock[] {
  const parts = diffChars(oldPara, newPara);
  const sameLength = parts
    .filter((part) => !part.added && !part.removed)
    .reduce((total, part) => total + part.value.length, 0);
  const similarity = sameLength / Math.max(oldPara.length, newPara.length, 1);
  if (similarity < MIN_INLINE_SIMILARITY) {
    return [
      { kind: "removed", text: oldPara },
      { kind: "added", text: newPara },
    ];
  }
  const segments: DiffSegment[] = parts.map((part) => ({
    type: part.added ? "ins" : part.removed ? "del" : "same",
    text: part.value,
  }));
  return [{ kind: "changed", segments }];
}

export function computeDiffBlocks(oldText: string, newText: string): DiffBlock[] {
  const hunks = diffArrays(splitBlocks(oldText), splitBlocks(newText));
  const blocks: DiffBlock[] = [];
  let index = 0;
  while (index < hunks.length) {
    const hunk = hunks[index];
    if (!hunk) break;
    const next = hunks[index + 1];
    if (hunk.removed && next?.added) {
      // 相邻的删除+新增视为“替换”：按下标配对做字符级 diff，多出的段按整段删/增。
      const pairCount = Math.min(hunk.value.length, next.value.length);
      for (let p = 0; p < pairCount; p += 1) {
        blocks.push(...pairBlocks(hunk.value[p]!, next.value[p]!));
      }
      for (let p = pairCount; p < hunk.value.length; p += 1) {
        blocks.push({ kind: "removed", text: hunk.value[p]! });
      }
      for (let p = pairCount; p < next.value.length; p += 1) {
        blocks.push({ kind: "added", text: next.value[p]! });
      }
      index += 2;
      continue;
    }
    for (const text of hunk.value) {
      blocks.push(hunk.removed
        ? { kind: "removed", text }
        : hunk.added
          ? { kind: "added", text }
          : { kind: "same", text });
    }
    index += 1;
  }
  return blocks;
}

export function diffHasChanges(blocks: DiffBlock[]): boolean {
  return blocks.some((block) => block.kind !== "same");
}

function renderBlock(block: DiffBlock): string {
  if (block.kind === "same") return `<div class="diff-para">${escapeHtml(block.text)}</div>`;
  if (block.kind === "added") {
    return `<div class="diff-para diff-para--added"><ins>${escapeHtml(block.text)}</ins></div>`;
  }
  if (block.kind === "removed") {
    return `<div class="diff-para diff-para--removed"><del>${escapeHtml(block.text)}</del></div>`;
  }
  const inner = block.segments
    .map((segment) => {
      const text = escapeHtml(segment.text);
      if (segment.type === "ins") return `<ins>${text}</ins>`;
      if (segment.type === "del") return `<del>${text}</del>`;
      return text;
    })
    .join("");
  return `<div class="diff-para diff-para--changed">${inner}</div>`;
}

export interface DiffRenderOptions {
  /** 连续未变段达到该数量才折叠。 */
  foldMinRun?: number;
  /** 折叠时头尾各保留几段作为上下文。 */
  foldContext?: number;
}

export function renderDiffHtml(blocks: DiffBlock[], options: DiffRenderOptions = {}): string {
  const foldMinRun = options.foldMinRun ?? 4;
  const foldContext = options.foldContext ?? 1;
  const html: string[] = [];
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index]!;
    if (block.kind !== "same") {
      html.push(renderBlock(block));
      index += 1;
      continue;
    }
    let runEnd = index;
    while (runEnd < blocks.length && blocks[runEnd]!.kind === "same") runEnd += 1;
    const runLength = runEnd - index;
    if (runLength >= foldMinRun) {
      for (let k = index; k < index + foldContext; k += 1) html.push(renderBlock(blocks[k]!));
      const foldedCount = runLength - foldContext * 2;
      const folded = blocks
        .slice(index + foldContext, runEnd - foldContext)
        .map(renderBlock)
        .join("");
      html.push(
        `<button class="diff-fold" type="button" aria-expanded="false">…未改动 ${foldedCount} 段，点击展开…</button>`,
        `<div class="diff-fold-group" hidden>${folded}</div>`,
      );
      for (let k = runEnd - foldContext; k < runEnd; k += 1) html.push(renderBlock(blocks[k]!));
    } else {
      for (let k = index; k < runEnd; k += 1) html.push(renderBlock(blocks[k]!));
    }
    index = runEnd;
  }
  return html.join("");
}
